import { useEffect, useState } from "react";
import api, { formatRand, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { ViewHeader } from "@/components/admin/AdminShared";
import { ProductFormDialog, emptyProductForm, productToForm } from "@/components/admin/ProductFormDialog";

export const ProductsView = ({ onBack, onChanged }) => {
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProductForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyProductForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm(productToForm(p));
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        type: form.type,
        price: parseFloat(form.price) || 0,
        images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
        sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colours: form.colours
          .split("\n")
          .map((l) => l.split("|").map((x) => x.trim()))
          .filter(([name]) => name)
          .map(([name, image]) => ({ name, image: image || null })),
        description: form.description,
        featured: form.featured,
        in_stock: form.in_stock ?? true,
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setDialogOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
    onChanged?.();
  };

  return (
    <div data-testid="admin-products-view">
      <ViewHeader
        onBack={onBack}
        title={`Inventory (${products.length})`}
        subtitle="Add, edit or delete products in the online store."
        action={
          <button data-testid="admin-add-product-button" onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full brand-gradient text-white font-semibold hover:opacity-90">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        }
      />
      <div className="bg-white rounded-2xl border border-purple-100 overflow-hidden overflow-x-auto">
        <Table data-testid="admin-inventory-table">
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Colours</TableHead>
              <TableHead>Sizes</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <img src={mediaUrl(p.images?.[0])} alt="" className="w-10 h-12 rounded object-contain bg-purple-50 p-0.5" />
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.item_code} · {p.type}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="capitalize text-sm">{p.category}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[160px]">{(p.colours || []).map((c) => c.name).join(", ")}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[160px]">{(p.sizes || []).join(", ")}</TableCell>
                <TableCell className="font-semibold text-sm">{formatRand(p.price)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-purple-50 text-purple-700">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ProductFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} form={form} setForm={setForm} onSave={save} saving={saving} />
    </div>
  );
};
